import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  FaEdit,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEyeSlash,
  FaEye,
  FaDownload,
} from "react-icons/fa";
import {
  updateProfile,
  changePassword,
  resendVerification,
} from "../../../store/slices/auth/asyncThunks";
import {
  selectUser,
  selectUserInfoPending,
  selectChangePasswordPending,
  selectResendPending,
} from "../../../store/slices/auth/slice";
import { getUserOrders } from "../../../store/slices/orders/asyncThunks";
import {
  selectOrders,
  selectOrdersLoading,
  selectOrdersError,
} from "../../../store/slices/orders/slice";
import { getUserApplications } from "../../../store/slices/certificates/asyncThunks";
import {
  selectUserApplications,
  selectApplicationsLoading,
  selectCertificatesError,
} from "../../../store/slices/certificates/slice";
import certificateService from "../../../api/services/certificate";
import {
  PageTitle,
  ProfileHeader,
  ProfileCard,
  AvatarSection,
  Avatar,
  UserInfo,
  UserName,
  UserRole,
  JoinDate,
  ProfileContent,
  Section,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  EditForm,
  FormGroup,
  Label,
  Input,
  FormActions,
  SaveButton,
  CancelButton,
  EmailStatusContainer,
  EmailStatusText,
  EmailStatusIcon,
  VerifyEmailButton,
  DisabledInput,
  EmailInfoValue,
  EmailInfoIcon,
  SectionLoading,
  SectionMessage,
  RecordsList,
  RecordCard,
  RecordHeader,
  RecordTitle,
  RecordDate,
  RecordDetails,
  RecordFooter,
  RecordAmount,
  StatusBadge,
} from "./ProfilePage.styles";
import { PasswordGroup, PasswordToggle } from "../../AuthPage/AuthPage.styles";
import { getInitials, priceFormatter } from "../../../utils/utils";

const ORDER_STATUS_LABELS = {
  pending: "Na čekanju",
  processing: "U obradi",
  completed: "Završena",
  canceled: "Otkazana",
  failed: "Neuspešna",
  refunded: "Refundirana",
};

const CERTIFICATE_STATUS_LABELS = {
  pending: "Na obradi",
  approved: "Odobren",
  rejected: "Odbijen",
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectUserInfoPending);
  const isChangePasswordLoading = useSelector(selectChangePasswordPending);
  const isResendLoading = useSelector(selectResendPending);

  const orders = useSelector(selectOrders);
  const ordersLoading = useSelector(selectOrdersLoading);
  const ordersError = useSelector(selectOrdersError);

  const userApplicationsData = useSelector(selectUserApplications);
  const applicationsLoading = useSelector(selectApplicationsLoading);
  const certificatesError = useSelector(selectCertificatesError);

  const applications = Array.isArray(userApplicationsData)
    ? userApplicationsData
    : userApplicationsData?.applications || [];

  const [downloadingId, setDownloadingId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [originalData, setOriginalData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  // Password change state
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const infoRef = useRef(null);

  useEffect(() => {
    if (isEditing && infoRef.current) {
      infoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isEditing]);

  useEffect(() => {
    dispatch(getUserOrders());
    dispatch(getUserApplications());
  }, [dispatch]);

  const handleDownloadCertificate = async (applicationId) => {
    setDownloadingId(applicationId);
    try {
      await certificateService.downloadCertificate(applicationId);
    } catch (error) {
      toast.error(error.message || "Greška prilikom preuzimanja sertifikata");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEdit = () => {
    const initialData = {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
    };
    setFormData(initialData);
    setOriginalData(initialData);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phone: formData.phone.trim(),
    };

    if (!trimmedData.firstName || !trimmedData.lastName) {
      toast.error("Ime i prezime ne mogu biti prazni");
      return;
    }

    try {
      await dispatch(updateProfile(trimmedData)).unwrap();
      toast.success("Profil je uspešno ažuriran!");
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message || "Greška prilikom ažuriranja profila");
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
  };

  // Check if form data has changed
  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Nova šifra se ne slaže sa potvrdom");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error("Nova šifra mora imati najmanje 8 karaktera");
      return;
    }

    if (
      !/[a-z]/.test(passwordData.newPassword) ||
      !/[A-Z]/.test(passwordData.newPassword) ||
      !/\d/.test(passwordData.newPassword)
    ) {
      toast.error(
        "Nova šifra mora sadržati bar jedno malo slovo, jedno veliko slovo i jedan broj",
      );
      return;
    }

    try {
      await dispatch(
        changePassword({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        }),
      ).unwrap();
      toast.success("Šifra je uspešno promenjena!");
      setShowPasswordChange(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast.error(error.message || "Greška prilikom menjanja šifre");
    }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswordChange(false);
  };

  const handleVerifyEmail = async () => {
    try {
      await dispatch(resendVerification({ email: user?.email })).unwrap();
      toast.success(
        "Verifikacioni email je poslat! Proverite svoju email kutiju.",
      );
    } catch (error) {
      toast.error(
        error.message || "Greška prilikom slanja verifikacionog email-a",
      );
    }
  };

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (!user) {
    return <div>Učitavanje...</div>;
  }

  return (
    <>
      <PageTitle>Moj Profil</PageTitle>

      <ProfileHeader>
        <ProfileCard>
          <AvatarSection>
            <Avatar>{getInitials(user)}</Avatar>
          </AvatarSection>

          <UserInfo>
            <UserName>
              {[user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
                (user?.email?.length > 15
                  ? user.email.slice(0, 15) + "..."
                  : user?.email)}
            </UserName>
            <UserRole>Beauty Student</UserRole>
            <JoinDate>
              <FaCalendarAlt />
              Član od{" "}
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString("sr-latn-RS")
                : "Nepoznato"}
            </JoinDate>
          </UserInfo>

          <SaveButton onClick={handleEdit}>
            <div>
              <FaEdit width={24} height={24} />
            </div>
            Uredi Profil
          </SaveButton>
        </ProfileCard>
      </ProfileHeader>

      <ProfileContent>
        <Section ref={infoRef}>
          <SectionTitle>Lične Informacije</SectionTitle>

          {isEditing ? (
            <EditForm>
              <FormGroup>
                <Label>Ime</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  placeholder="Unesite ime"
                />
              </FormGroup>

              <FormGroup>
                <Label>Prezime</Label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  placeholder="Unesite prezime"
                />
              </FormGroup>

              <FormGroup>
                <Label>Email Adresa</Label>
                <DisabledInput
                  type="email"
                  value={user?.email || ""}
                  disabled
                  placeholder="Email se ne može menjati"
                />
                <EmailStatusContainer>
                  {user?.isEmailVerified ? (
                    <>
                      <EmailStatusIcon $verified={true}>
                        <FaCheckCircle />
                      </EmailStatusIcon>
                      <EmailStatusText $verified={true}>
                        Email je potvrđen
                      </EmailStatusText>
                    </>
                  ) : (
                    <>
                      <EmailStatusIcon $verified={false}>
                        <FaExclamationTriangle />
                      </EmailStatusIcon>
                      <EmailStatusText $verified={false}>
                        Email nije potvrđen
                      </EmailStatusText>
                      <VerifyEmailButton
                        onClick={handleVerifyEmail}
                        disabled={isResendLoading}
                      >
                        <FaEnvelope />
                        {isResendLoading ? "Šalje..." : "Pošalji verifikaciju"}
                      </VerifyEmailButton>
                    </>
                  )}
                </EmailStatusContainer>
              </FormGroup>

              <FormGroup>
                <Label>Telefon</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="Unesite broj telefona"
                />
              </FormGroup>

              <FormActions>
                <SaveButton
                  onClick={handleSave}
                  disabled={isLoading || !hasChanges()}
                >
                  <FaSave />
                  {isLoading ? "Čuvanje..." : "Sačuvaj"}
                </SaveButton>
                <CancelButton onClick={handleCancel} disabled={isLoading}>
                  <FaTimes />
                  Otkaži
                </CancelButton>
              </FormActions>
            </EditForm>
          ) : (
            <InfoGrid>
              <InfoItem>
                <InfoLabel>
                  <FaEnvelope />
                  Email
                  <EmailInfoIcon $verified={user?.isEmailVerified}>
                    {user?.isEmailVerified ? (
                      <FaCheckCircle />
                    ) : (
                      <FaExclamationTriangle />
                    )}
                  </EmailInfoIcon>
                </InfoLabel>
                <EmailInfoValue>{user?.email}</EmailInfoValue>
                {!user?.isEmailVerified && (
                  <EmailStatusContainer>
                    <EmailStatusIcon $verified={false}>
                      <FaExclamationTriangle />
                    </EmailStatusIcon>
                    <EmailStatusText $verified={false}>
                      Email nije potvrđen
                    </EmailStatusText>
                    <VerifyEmailButton
                      onClick={handleVerifyEmail}
                      disabled={isResendLoading}
                    >
                      <FaEnvelope />
                      {isResendLoading ? "Šalje..." : "Pošalji verifikaciju"}
                    </VerifyEmailButton>
                  </EmailStatusContainer>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>
                  <FaPhone />
                  Telefon
                </InfoLabel>
                <InfoValue>{user?.phone || "Nije uneto"}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>
                  <FaCalendarAlt />
                  Datum Registracije
                </InfoLabel>
                <InfoValue>
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString("sr-latn-RS")
                    : "Nepoznato"}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          )}
        </Section>

        <Section>
          <SectionTitle>Bezbednost</SectionTitle>

          {!showPasswordChange ? (
            <SaveButton onClick={() => setShowPasswordChange(true)}>
              Promeni Šifru
            </SaveButton>
          ) : (
            <EditForm>
              <FormGroup>
                <Label>Trenutna Šifra</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.currentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      handlePasswordInputChange(
                        "currentPassword",
                        e.target.value,
                      )
                    }
                    placeholder="Unesite trenutnu šifru"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("currentPassword")}
                  >
                    {showPassword.currentPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <FormGroup>
                <Label>Nova Šifra</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.newPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordInputChange("newPassword", e.target.value)
                    }
                    placeholder="Unesite novu šifru"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("newPassword")}
                  >
                    {showPassword.newPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <FormGroup>
                <Label>Potvrdi Novu Šifru</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword.confirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handlePasswordInputChange(
                        "confirmPassword",
                        e.target.value,
                      )
                    }
                    placeholder="Potvrdite novu šifru"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => togglePassword("confirmPassword")}
                  >
                    {showPassword.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <FormActions>
                <SaveButton
                  onClick={handleChangePassword}
                  disabled={
                    isChangePasswordLoading ||
                    !passwordData.currentPassword ||
                    !passwordData.newPassword ||
                    !passwordData.confirmPassword
                  }
                >
                  <FaSave />
                  {isChangePasswordLoading ? "Menjanje..." : "Promeni Šifru"}
                </SaveButton>
                <CancelButton
                  onClick={handleCancelPasswordChange}
                  disabled={isChangePasswordLoading}
                >
                  <FaTimes />
                  Otkaži
                </CancelButton>
              </FormActions>
            </EditForm>
          )}
        </Section>

        <Section>
          <SectionTitle>Moje Porudžbine</SectionTitle>

          {ordersLoading ? (
            <SectionLoading>
              <l-line-spinner
                size="24"
                stroke="2"
                speed="1"
                color="var(--accent)"
              />
            </SectionLoading>
          ) : ordersError ? (
            <SectionMessage>
              Greška prilikom učitavanja porudžbina. Pokušajte ponovo kasnije.
            </SectionMessage>
          ) : orders.length === 0 ? (
            <SectionMessage>Još uvek nemate porudžbina.</SectionMessage>
          ) : (
            <RecordsList>
              {orders.map((order) => {
                const courseNames = (order.items || [])
                  .map((item) => item.courseTitle || item.course?.title)
                  .filter(Boolean)
                  .join(", ");

                return (
                  <RecordCard key={order.id}>
                    <RecordHeader>
                      <RecordTitle>#{order.orderNumber}</RecordTitle>
                      <RecordDate>
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleDateString(
                              "sr-latn-RS",
                            )
                          : ""}
                      </RecordDate>
                    </RecordHeader>
                    {courseNames && <RecordDetails>{courseNames}</RecordDetails>}
                    <RecordFooter>
                      <RecordAmount>
                        {priceFormatter(order.total)} RSD
                      </RecordAmount>
                      <StatusBadge $status={order.status}>
                        {ORDER_STATUS_LABELS[order.status] || order.status}
                      </StatusBadge>
                    </RecordFooter>
                  </RecordCard>
                );
              })}
            </RecordsList>
          )}
        </Section>

        <Section>
          <SectionTitle>Moji Sertifikati</SectionTitle>

          {applicationsLoading ? (
            <SectionLoading>
              <l-line-spinner
                size="24"
                stroke="2"
                speed="1"
                color="var(--accent)"
              />
            </SectionLoading>
          ) : certificatesError ? (
            <SectionMessage>
              Greška prilikom učitavanja sertifikata. Pokušajte ponovo kasnije.
            </SectionMessage>
          ) : applications.length === 0 ? (
            <SectionMessage>Još uvek nemate sertifikata.</SectionMessage>
          ) : (
            <RecordsList>
              {applications.map((application) => (
                <RecordCard key={application.id}>
                  <RecordHeader>
                    <RecordTitle>
                      {application.course?.title || "Kurs"}
                    </RecordTitle>
                    <RecordDate>
                      {application.appliedAt
                        ? new Date(application.appliedAt).toLocaleDateString(
                            "sr-latn-RS",
                          )
                        : ""}
                    </RecordDate>
                  </RecordHeader>
                  <RecordFooter>
                    <StatusBadge $status={application.status}>
                      {CERTIFICATE_STATUS_LABELS[application.status] ||
                        application.status}
                    </StatusBadge>
                    {application.status === "approved" && (
                      <SaveButton
                        onClick={() =>
                          handleDownloadCertificate(application.id)
                        }
                        disabled={downloadingId === application.id}
                      >
                        <FaDownload />
                        {downloadingId === application.id
                          ? "Preuzimanje..."
                          : "Preuzmi"}
                      </SaveButton>
                    )}
                  </RecordFooter>
                </RecordCard>
              ))}
            </RecordsList>
          )}
        </Section>
      </ProfileContent>
    </>
  );
};

export default ProfilePage;
